import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { EnvConfigLoaderService } from '@app/shared/services/app/env-config-loader.service';
import { EnabledModule, GetEnabledModulesResult } from './module-registry.models';

/**
 * App-platform module registry (spec docs/superpowers/specs/2026-09-05-app-platform-registry-design.md §6.1).
 * Loaded once per session; drives module routing (moduleRouteGuard), the generic AppHostComponent,
 * and closed-by-default gating of module routes (PermissionService.registerGatedRoutes).
 * Hand-written against erpApiUrl (not the generated @api-client) so the shell needs no client regeneration.
 */
@Injectable({ providedIn: 'root' })
export class ModuleRegistryService {
  private registry$: Observable<EnabledModule[]> | null = null;
  private launchable$: Observable<EnabledModule[]> | null = null;
  private snapshot: EnabledModule[] = [];

  constructor(private http: HttpClient, private env: EnvConfigLoaderService) {}

  load(): Observable<EnabledModule[]> {
    if (!this.registry$) {
      const base = this.env.getSettings('env')?.erpApiUrl;
      this.registry$ = this.http.get<GetEnabledModulesResult>(`${base}/Modules/enabled`).pipe(
        map(r => r?.items ?? []),
        catchError(err => {
          // Fail soft: no registry → no dynamic routes. Static routes and the menu keep working.
          console.error('Failed to load module registry:', err);
          return of([] as EnabledModule[]);
        }),
        tap(items => (this.snapshot = items)),
        shareReplay(1)
      );
    }
    return this.registry$;
  }

  /**
   * Every module the tenant has, native (Angular) ones included, each with its menuUid.
   * Feeds the launchpad and the /app/:key route (ZEY-2419). Kept apart from load() so the
   * registry that drives micro-UI hosting and route gating is unchanged.
   */
  launchable(): Observable<EnabledModule[]> {
    if (!this.launchable$) {
      const base = this.env.getSettings('env')?.erpApiUrl;
      this.launchable$ = this.http.get<GetEnabledModulesResult>(`${base}/Modules/enabled`, { params: { includeNative: 'true' } }).pipe(
        map(r => r?.items ?? []),
        catchError(err => {
          console.error('Failed to load launchable modules:', err);
          this.launchable$ = null; // let the launchpad's retry ask again
          return of([] as EnabledModule[]);
        }),
        shareReplay(1)
      );
    }
    return this.launchable$;
  }

  /** Synchronous view of the last loaded registry (empty until load() has emitted). */
  modules(): EnabledModule[] {
    return this.snapshot;
  }

  byKey(key: string): EnabledModule | undefined {
    return this.snapshot.find(m => m.key === key);
  }

  /** Where the iframe loads from: a local override from env-config.json, else shell origin + uiPath. */
  iframeBaseUrl(mod: EnabledModule): string {
    const overrides = this.env.getSettings('env')?.moduleUiUrlOverrides as Record<string, string> | undefined;
    return overrides?.[mod.key] ?? `${window.location.origin}${mod.uiPath}`;
  }
}
